import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { isMessengerNetwork, type MessengerLinks, type MessengerNetwork, type PrintPartnerKey } from '@lite-workspace/dto-user';
import { parseCreateProjectPreference, parseTextStyles } from './identity.models';
import type { AuthType, Contact, OnboardingProgress, ProfilePatch, RetouchPrefs, UserProfile } from './identity.models';

interface ProfileDto {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  authType: AuthType;
  contacts: Contact[];
  onboardingProgress?: Partial<OnboardingProgress> | null;
  createProjectPreference?: unknown;
  textStyles?: unknown;
  partnerKey?: PrintPartnerKey | null;
  marketingUnsubscribed?: boolean;
  messengers?: Partial<MessengerLinks>;
  messengerConnect?: unknown;
  booksView?: string | null;
  retouchPrefs?: RetouchPrefs | null;
}

function toOnboardingProgress(dto: Partial<OnboardingProgress> | null | undefined): OnboardingProgress {
  return {
    completed: dto?.completed ?? [],
    dismissed: dto?.dismissed ?? [],
    viewed: dto?.viewed ?? [],
    // Корзину избранного не достраиваем: ненаписанная она и должна приходить пустотой, а не пустым
    // списком, — на этой разнице стоят умолчания «Избранного» у новичка.
    favorites: dto?.favorites,
  };
}

function toUserProfile(dto: ProfileDto): UserProfile {
  return {
    id: dto.id,
    email: dto.email,
    firstName: dto.firstName,
    lastName: dto.lastName,
    authType: dto.authType,
    // otp-only accounts have no password set; all other authTypes include a password
    hasPassword: dto.authType !== 'otp',
    contacts: dto.contacts,
    onboardingProgress: toOnboardingProgress(dto.onboardingProgress),
    createProjectPreference: parseCreateProjectPreference(dto.createProjectPreference),
    textStyles: parseTextStyles(dto.textStyles),
    partnerKey: dto.partnerKey ?? null,
    marketingUnsubscribed: dto.marketingUnsubscribed,
    messengers: dto.messengers,
    messengerConnect: Array.isArray(dto.messengerConnect) ? dto.messengerConnect.filter(isMessengerNetwork) : [],
    booksView: dto.booksView === 'cards' || dto.booksView === 'list' ? dto.booksView : null,
    retouchPrefs: dto.retouchPrefs ?? null,
  };
}

@Injectable({ providedIn: 'root' })
export class IdentityApiService {
  private readonly http = inject(HttpClient);

  getProfile(): Observable<UserProfile> {
    return this.http.get<ProfileDto>('/api/users/me/profile').pipe(map(toUserProfile));
  }

  updateProfile(patch: ProfilePatch): Observable<UserProfile> {
    return this.http.patch<ProfileDto>('/api/users/me/profile', patch).pipe(map(toUserProfile));
  }

  setPassword(body: { password?: string; authType?: AuthType }): Observable<void> {
    return this.http.patch<void>('/api/users/me/password', body);
  }

  /** One-time link to the support channel of that network; the first message there connects the account. */
  connectMessenger(network: MessengerNetwork): Observable<string> {
    return this.http.post<{ url: string }>(`/api/users/me/messengers/${network}/connect`, {}).pipe(map(res => res.url));
  }

  disconnectMessenger(network: MessengerNetwork): Observable<void> {
    return this.http.delete<void>(`/api/users/me/messengers/${network}`);
  }

  dismissOnboardingFlow(flowId: string): Observable<OnboardingProgress> {
    return this.postOnboarding(flowId, 'dismiss');
  }

  completeOnboardingFlow(flowId: string): Observable<OnboardingProgress> {
    return this.postOnboarding(flowId, 'complete');
  }

  markLessonViewed(flowId: string): Observable<OnboardingProgress> {
    return this.postOnboarding(flowId, 'viewed');
  }

  unmarkLessonViewed(flowId: string): Observable<OnboardingProgress> {
    return this.deleteOnboarding(flowId, 'viewed');
  }

  markLessonFavorite(flowId: string): Observable<OnboardingProgress> {
    return this.postOnboarding(flowId, 'favorite');
  }

  unmarkLessonFavorite(flowId: string): Observable<OnboardingProgress> {
    return this.deleteOnboarding(flowId, 'favorite');
  }

  private postOnboarding(flowId: string, action: 'complete' | 'dismiss' | 'viewed' | 'favorite'): Observable<OnboardingProgress> {
    return this.http
      .post<Partial<OnboardingProgress>>(`/api/users/me/onboarding/${encodeURIComponent(flowId)}/${action}`, {})
      .pipe(map(toOnboardingProgress));
  }

  private deleteOnboarding(flowId: string, action: 'viewed' | 'favorite'): Observable<OnboardingProgress> {
    return this.http
      .delete<Partial<OnboardingProgress>>(`/api/users/me/onboarding/${encodeURIComponent(flowId)}/${action}`)
      .pipe(map(toOnboardingProgress));
  }
}
